import { ReactNode } from 'react';
import OverflowRow from './OverflowRow';
import { useTooltip } from './Tooltip';

interface Props<K extends string> {
  options: { key: K; label: ReactNode; inert?: boolean; /** Tooltip text on hover/focus. */ help?: string; /** Leading icon (design View toggle). */ icon?: ReactNode; /** Native title (full name behind an abbreviated label). */ title?: string; /** Trailing count (design `.seg .sc`). */ count?: number }[];
  value: K;
  onChange: (k: K) => void;
  label: string;
  /** Single row with prev/next arrows when the options overflow. */
  scroll?: boolean;
  /** Sans, weight 500 labels (design override on some segmented controls). */
  dense?: boolean;
  /** Expose as a tablist (role=tab, aria-selected) instead of pressed buttons. */
  tabs?: boolean;
  /** id of an element (e.g. an inline error) that describes the group. */
  describedBy?: string;
  /** Full width, options share the row equally (design `.seg` with `flex:1` buttons). */
  fill?: boolean;
}

type Opt = Props<string>['options'][number];

function SegButton({ o, active, tabs, dense, scroll, fill, onPick }: { o: Opt; active: boolean; tabs: boolean; dense: boolean; scroll: boolean; fill: boolean; onPick: () => void }) {
  const { ref, triggerProps, tooltip } = useTooltip<HTMLButtonElement>(o.help);
  return (
    <>
      <button ref={ref} type="button" {...(tabs ? { role: 'tab', 'aria-selected': active } : { 'aria-pressed': active })} aria-disabled={o.inert ? true : undefined} title={o.help ? undefined : o.title} onClick={onPick}
        {...(o.help ? triggerProps : {})}
        className={`${o.inert ? 'cursor-not-allowed opacity-50 ' : ''}min-h-[36px] rounded-[8px] px-[13px] py-0 text-[13px] transition-colors ${dense ? 'font-medium' : 'font-mono font-semibold'} ${scroll ? 'shrink-0 whitespace-nowrap ' : ''}${fill ? 'min-w-0 flex-1 ' : ''}${
          active ? 'bg-navy text-white' : 'bg-transparent text-muted hover:text-ink'}`}>
        {o.icon ? <span className="inline-flex items-center gap-1.5">{o.icon}{o.label}</span> : o.label}
        {o.count !== undefined && <span data-testid="seg-count" className="ml-1 opacity-75">{o.count}</span>}
      </button>
      {tooltip}
    </>
  );
}

export default function Segmented<K extends string>({ options, value, onChange, label, scroll = false, dense = false, tabs = false, describedBy, fill = false }: Props<K>) {
  if (options.length === 0) return null;
  const buttons = (
    <>
      {options.map((o) => (
        <SegButton key={o.key} o={o} active={o.key === value} tabs={tabs} dense={dense} scroll={scroll} fill={fill} onPick={() => onChange(o.key)} />
      ))}
    </>
  );
  if (scroll) {
    return (
      <OverflowRow inline role={tabs ? 'tablist' : 'group'} label={label} describedBy={describedBy} activeKey={value} className="rounded-[12px] border border-line-strong bg-surface p-1" rowClassName="gap-0.5">
        {buttons}
      </OverflowRow>
    );
  }
  return (
    <div role={tabs ? 'tablist' : 'group'} aria-label={label} aria-describedby={describedBy} aria-invalid={describedBy ? true : undefined} className={`${fill ? 'flex w-full' : 'inline-flex'} max-w-full flex-wrap gap-0.5 rounded-[12px] border border-line-strong bg-surface p-1`}>
      {buttons}
    </div>
  );
}
